import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp, API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Advice() {
  const { t } = useApp();
  const [activeCat, setActiveCat] = useState("pregnant");
  const [staticAdvice, setStaticAdvice] = useState([]);
  const [aiTopic, setAiTopic] = useState("alimentation");
  const [aiCategory, setAiCategory] = useState("pregnant");
  const [aiLang, setAiLang] = useState("fr");
  const [aiResult, setAiResult] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/advice/static`, { params: { category: activeCat } }).then((r) => setStaticAdvice(r.data.items));
  }, [activeCat]);

  const generate = async () => {
    setAiLoading(true);
    setAiResult("");
    try {
      const r = await axios.post(`${API}/advice/generate`, { category: aiCategory, topic: aiTopic, language: aiLang });
      setAiResult(r.data.content);
      toast.success("Conseil généré par IA");
    } catch (err) {
      toast.error("Erreur génération IA");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="advice-page">
      <h1 className="text-3xl font-[Outfit] font-bold">{t.dash.advice}</h1>

      <Tabs defaultValue="static">
        <TabsList>
          <TabsTrigger value="static" data-testid="tab-static"><BookOpen className="w-4 h-4 mr-2"/> {t.dash.static_lib}</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai"><Sparkles className="w-4 h-4 mr-2"/> {t.dash.generate_ai}</TabsTrigger>
        </TabsList>

        <TabsContent value="static" className="space-y-5 pt-5">
          <div className="flex gap-2 flex-wrap">
            {[
              { k: "pregnant", l: t.dash.pregnant },
              { k: "child", l: t.dash.child },
              { k: "elderly", l: t.dash.elderly },
              { k: "general", l: t.dash.general },
            ].map((c) => (
              <button
                key={c.k}
                onClick={() => setActiveCat(c.k)}
                data-testid={`cat-tab-${c.k}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  activeCat === c.k ? "bg-[#1E4631] text-white" : "bg-white border border-[#E5EBE7] text-[#5C6B61] hover:border-[#1E4631]"
                }`}
              >{c.l}</button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {staticAdvice.map((a, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#E5EBE7] p-6 hover:shadow-md transition" data-testid={`advice-static-${i}`}>
                <h3 className="font-[Outfit] font-semibold text-lg text-[#1E4631]">{a.title}</h3>
                <p className="mt-2 text-sm text-[#5C6B61] leading-relaxed">{a.content}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="ai" className="space-y-5 pt-5">
          <div className="bg-white rounded-xl border border-[#E5EBE7] p-6 space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-[#5C6B61] uppercase tracking-wide">{t.dash.category}</label>
                <Select value={aiCategory} onValueChange={setAiCategory}>
                  <SelectTrigger data-testid="ai-category"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pregnant">{t.dash.pregnant}</SelectItem>
                    <SelectItem value="child">{t.dash.child}</SelectItem>
                    <SelectItem value="elderly">{t.dash.elderly}</SelectItem>
                    <SelectItem value="general">{t.dash.general}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C6B61] uppercase tracking-wide">{t.dash.topic}</label>
                <Input value={aiTopic} onChange={(e)=>setAiTopic(e.target.value)} data-testid="ai-topic"/>
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C6B61] uppercase tracking-wide">{t.dash.language}</label>
                <Select value={aiLang} onValueChange={setAiLang}>
                  <SelectTrigger data-testid="ai-lang"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="ln">Lingala</SelectItem>
                    <SelectItem value="sw">Swahili</SelectItem>
                    <SelectItem value="ts">Tshiluba</SelectItem>
                    <SelectItem value="kg">Kikongo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={generate} disabled={aiLoading} className="bg-[#D96C4A] hover:bg-[#B85A3D] text-white" data-testid="ai-generate">
              {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Sparkles className="w-4 h-4 mr-2"/>}
              {t.dash.generate_ai}
            </Button>
          </div>
          {aiResult && (
            <div className="bg-[#1E4631]/5 border border-[#1E4631]/20 rounded-xl p-6" data-testid="ai-result">
              <div className="flex items-center gap-2 text-xs text-[#1E4631] uppercase tracking-wide font-bold mb-3">
                <Sparkles className="w-3.5 h-3.5"/> Claude AI
              </div>
              <pre className="whitespace-pre-wrap text-sm text-[#1A1D1A] leading-relaxed font-[Manrope]">{aiResult}</pre>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
